import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ITimeBlockListing } from '../../../../entities-generated/models/time-block/time-block-listing';
import { where, groupBy, forEach, min, max } from '../../../../framework/utils/collection';
import { toDate, toUnixTimestamp } from '../../../../framework/utils/date-time';
import { TimeBlockGroup } from '../../entities/time-block-group';
import { TimeBlockGroupListing } from '../time-group-listing';

interface ITimeBlockListProps {
  timeBlocks: ITimeBlockListing[];
  onSelectionChange: (selectionRange: [number, number] | null) => void;
  clearSelectionApi: {} | undefined;
}

export const TimeBlockList: React.FunctionComponent<ITimeBlockListProps> = (props) => {
  const { timeBlocks: timeBlocksFromProps, clearSelectionApi: clearSelection, onSelectionChange } = props;
  const [timeBlocks, setTimeBlocks] = useState<ITimeBlockListing[]>([]);
  const selectedBlocks = useMemo(() => where(timeBlocks, t => t.isSelected), [timeBlocks]);
  const blockGroups = useMemo(() => {
    const objGroups = groupBy(timeBlocks, t => t.date);
    const arrGroups: ITimeBlockListing[][] = [];
    forEach(objGroups, group => {
      arrGroups.push(group);
    });
    const bgs = arrGroups.map(group => new TimeBlockGroup(toDate(group[0].date), group));
    return bgs;
  }, [timeBlocks]);

  const prevTimeBlocksFromProps = useRef<ITimeBlockListing[] | undefined>(undefined);
  const prevClearSelection = useRef<{} | undefined>(undefined);

  // change to time blocks incoming from props
  useEffect(() => {
    if(timeBlocksFromProps !== prevTimeBlocksFromProps.current) {
      setTimeBlocks(timeBlocksFromProps);
      prevTimeBlocksFromProps.current = timeBlocksFromProps;
    }
  }, [timeBlocksFromProps]);

  useEffect(() => {
    if(!!clearSelection && timeBlocks.length > 0 && clearSelection !== prevClearSelection.current) {
      const tbs = [...timeBlocks];
      forEach(tbs, block => { block.isSelected = false; });
      setTimeBlocks(tbs);
      prevClearSelection.current = clearSelection;
    }
  }, [clearSelection, timeBlocks]);

  useEffect(() => {
    const bounds: [number, number] | null = selectedBlocks.length > 0
    ? [min(selectedBlocks, b => b.blockNumber), max(selectedBlocks, b => b.blockNumber)]
    : null;
    onSelectionChange(bounds);
  }, [selectedBlocks, onSelectionChange]);

  const onToggleSelection = (block: ITimeBlockListing) => {
    const tbs = [...timeBlocks];
    forEach(tbs, b => { b.isSelected = false; });

    if(selectedBlocks.length === 0) {
      block.isSelected = true;
    } else {
      const minBlock = min(selectedBlocks, b => b.blockNumber);
      const maxBlock = max(selectedBlocks, b => b.blockNumber);
      if(block.blockNumber !== minBlock && block.blockNumber !== maxBlock) {
        const selection = block.blockNumber < minBlock
          ? where(tbs, b => b.blockNumber >= block.blockNumber && b.blockNumber <= minBlock)
          : where(tbs, b => b.blockNumber >= minBlock && b.blockNumber <= block.blockNumber);
        forEach(selection, b => { b.isSelected = true; });
      }
    }
    
    setTimeBlocks(tbs);
  }

  return (
    <>
      {blockGroups.map(group =>
      <TimeBlockGroupListing
        key={toUnixTimestamp(group.date).toString()}
        group={group}
        onToggleSelection={onToggleSelection}
      />)}
    </>
  );
};
